import downArrow from "@assets/quick-news/down-arrow.svg";
import Paragraph from "@elements/Typography/Paragraph";
import { countWords, truncateWords } from "@helper/stringTransform";
import { ProcessedArticleBody } from "@typings/Opensearch";
import React, { type ReactElement, useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

const WORD_COUNT = 60;
const MAX_PARAGRAPHS = 4;
const TOGGLE_BUTTON_BUFFER = 25; // Buffer for toggle button

interface QnParagraphProps {
  articleBody: ProcessedArticleBody;
  toggleDescription: () => void;
  isDescriptionOpen: boolean;
  setReduceSize: React.Dispatch<React.SetStateAction<number>>;
}

export default function QnParagraph({
  articleBody,
  toggleDescription,
  isDescriptionOpen,
  setReduceSize,
}: QnParagraphProps): ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      if (!containerRef.current || !contentRef.current) return;

      const containerHeight = containerRef.current.clientHeight;
      const contentHeight = contentRef.current.offsetHeight;

      // ## TODO: Comeback to this place to resolve overflow issue.
      setIsOverflowing(contentHeight > containerHeight - TOGGLE_BUTTON_BUFFER);

      if (contentHeight > containerHeight - TOGGLE_BUTTON_BUFFER) {
        setReduceSize(contentHeight - containerHeight);
      }
    };

    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => {
      window.removeEventListener("resize", checkOverflow);
    };
  }, [setReduceSize]);

  const visibleParagraphs = articleBody.reduce<
    {
      element: (typeof articleBody)[number];
      index: number;
      wordsToShow: number;
    }[]
  >((acc, element, index) => {
    if (element.type !== "paragraph") return acc;
    if (acc.length >= MAX_PARAGRAPHS) return acc;

    const accumulatedWords = acc.reduce((sum, p) => sum + p.wordsToShow, 0);
    if (accumulatedWords >= WORD_COUNT) return acc;

    const wordsInParagraph = countWords(element.value ? element.value : "");
    if (wordsInParagraph < 1) return acc;

    const wordsToShow =
      accumulatedWords + wordsInParagraph > WORD_COUNT
        ? WORD_COUNT - accumulatedWords
        : wordsInParagraph;

    return [...acc, { element, index, wordsToShow }];
  }, []);

  return (
    <div
      ref={containerRef}
      className="qn-article-body-container relative h-[calc(100lvh-490px)] md:h-[calc(100lvh-660px)]"
    >
      <div
        ref={contentRef}
        data-testid="article-body"
        className="qn-article-body gap-3 grid relative"
      >
        {visibleParagraphs.map(({ element, index, wordsToShow }) => {
          if (element.type !== "paragraph") return null;
          return (
            <Paragraph
              key={element.id || index}
              testid={`paragraph-${index}`}
              id={element.id}
              className="qn-article-description text-text-body text-sm font-medium tracking-normal leading-7 mb-0 duration-500"
            >
              {truncateWords(element.value ? element.value : "", wordsToShow)}
              {wordsToShow < countWords(element.value ? element.value : "") &&
                "..."}
            </Paragraph>
          );
        })}

        {/* Toggle button */}
        {isOverflowing && (
          <button
            onClick={toggleDescription}
            className={`qn-article-expand z-10 fixed bottom-[70px] flex w-11/12 justify-center mx-auto items-center ${!isDescriptionOpen && "collapsed"}`}
            aria-label="Toggle description"
          >
            <img
              src={downArrow}
              alt="dropdown-arrow"
              width={22}
              height={22}
              className={twMerge(
                "z-40 transition-transform duration-500",
                isDescriptionOpen && "-rotate-180",
              )}
            />
          </button>
        )}
      </div>
    </div>
  );
}
